import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import SimpleLocationPicker from "../components/SimpleLocationPicker";
import { useLocation } from "../context/LocationContext";
import {
  Stethoscope,
  Pill,
  TestTube,
  Activity,
  User,
  MapPin,
  Award,
  Phone,
  ArrowRight,
  Heart,
  Shield,
  Clock,
  Sparkles,
  Tag,
  Zap,
  ArrowUpRight,
} from "lucide-react";
import { mockOffers, generateWhatsAppLink } from "../mock/mockData";

const Home = () => {
  const { currentLocation, getServiceMessage } = useLocation();
  const [subscribeForm, setSubscribeForm] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
  });
  const [isVisible, setIsVisible] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);

    // Counter animation for discounts
    const countTimer = setInterval(() => {
      setCount((prev) => {
        if (prev >= 100) {
          clearInterval(countTimer);
          return 100;
        }
        return prev + 5;
      });
    }, 50);

    return () => {
      clearTimeout(timer);
      clearInterval(countTimer);
    };
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    const whatsAppMessage = generateWhatsAppLink("subscription", {
      userName: subscribeForm.name,
      email: subscribeForm.email,
      phone: subscribeForm.phone,
      age: subscribeForm.age,
    });
    window.open(whatsAppMessage, "_blank");
  };

  const quickLinks = [
    {
      name: "Doctors",
      href: "/doctors",
      icon: Stethoscope,
      color: "bg-blue-500",
      description: "Find & Book Appointments",
    },
    {
      name: "Pharmacy",
      href: "/pharmacy",
      icon: Pill,
      color: "bg-green-500",
      description: "Order Medicines",
    },
    {
      name: "Lab Tests",
      href: "/lab",
      icon: TestTube,
      color: "bg-purple-500",
      description: "Book Lab Tests",
    },
    {
      name: "Vitals",
      href: "/vitals",
      icon: Activity,
      color: "bg-red-500",
      description: "Track Health",
    },
    {
      name: "Account",
      href: "/account",
      icon: User,
      color: "bg-orange-500",
      description: "Manage Profile",
    },
  ];

  const features = [
    {
      icon: Heart,
      title: "Expert Care",
      description: "Connect with verified doctors and healthcare professionals",
    },
    {
      icon: Shield,
      title: "Secure & Safe",
      description:
        "Your health data is protected with industry-standard security",
    },
    {
      icon: Clock,
      title: "24/7 Support",
      description: "Round-the-clock healthcare assistance via WhatsApp",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Location Selector Banner */}
      <section className="bg-orange-600 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <span className="text-white text-sm font-medium hidden md:block">
                Healthcare services in:
              </span>
              <div className="relative">
                <SimpleLocationPicker />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Discount Banner */}
      <section className="bg-gradient-to-r from-orange-50 via-white to-orange-50 py-6 border-b border-orange-200 relative overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-64 h-64 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-orange-300 rounded-full blur-2xl"></div>
        </div>

        {/* Animated Border */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent animate-pulse"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex flex-col lg:flex-row items-center justify-between gap-6 transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-8"
            }`}
          >
            {/* Left Content - Main Message */}
            <div className="text-center lg:text-left space-y-3">
              <div className="flex items-center justify-center lg:justify-start space-x-3">
                <div className="flex items-center space-x-2 bg-orange-100 rounded-full px-4 py-1.5 border border-orange-200">
                  <Sparkles className="w-4 h-4 text-orange-600 animate-pulse" />
                  <span className="text-orange-700 text-sm font-semibold">
                    EXCLUSIVE OFFERS
                  </span>
                </div>
                <div className="hidden md:block w-px h-6 bg-orange-200"></div>
                <p className="hidden md:block text-orange-600 text-sm font-medium">
                  Limited time only
                </p>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                Special Healthcare{" "}
                <span className="text-orange-600">Discounts</span>
              </h3>
            </div>

            {/* Center - Discount Cards */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Pharmacy Discount */}
              <div className="group relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-green-500 rounded-2xl blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-tilt"></div>
                <div className="relative bg-white rounded-xl p-4 border border-orange-200 shadow-sm hover:shadow-md transition-all duration-300 group-hover:scale-105">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-sm">
                      <Pill className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-baseline space-x-1">
                        <span className="text-2xl font-bold text-gray-900">
                          20%
                        </span>
                        <span className="text-green-600 font-semibold">
                          OFF
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Pharmacy Orders
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lab Discount */}
              <div className="group relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-purple-500 rounded-2xl blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-tilt"></div>
                <div className="relative bg-white rounded-xl p-4 border border-orange-200 shadow-sm hover:shadow-md transition-all duration-300 group-hover:scale-105">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center shadow-sm">
                      <TestTube className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-baseline space-x-1">
                        <span className="text-2xl font-bold text-gray-900">
                          50%
                        </span>
                        <span className="text-purple-600 font-semibold">
                          OFF
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Lab Packages</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - CTA */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              <Link to="/pharmacy" className="block">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105 group">
                  <span>Claim Offers</span>
                  <ArrowUpRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Button>
              </Link>
              <p className="text-xs text-gray-500 text-center lg:text-right">
                * Terms apply
              </p>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-orange-100">
          <div
            className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-1000 ease-out"
            style={{ width: `${count}%` }}
          />
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-50 to-orange-100 py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-white/20 backdrop-blur-sm"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Your Health,{" "}
                  <span className="text-orange-600">Our Priority</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-600 max-w-lg">
                  Complete healthcare solutions at your fingertips. Connect with
                  doctors, order medicines, book lab tests - all through
                  WhatsApp.
                </p>
              </div>

              {/* Offer Chips */}
              <div className="flex flex-wrap gap-3">
                {mockOffers.map((offer) => (
                  <Badge
                    key={offer.id}
                    className="bg-orange-600 text-white px-3 py-1.5 text-sm font-semibold"
                  >
                    {offer.title} {offer.subtitle}
                  </Badge>
                ))}
              </div>

              {/* Location Services */}
              <div className="bg-white/90 backdrop-blur-sm p-4 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Available in your area:
                </h4>
                <div className="space-y-1">
                  {currentLocation && getServiceMessage().length > 0 ? (
                    getServiceMessage().map((message, index) => (
                      <div key={index} className="text-sm text-gray-700">
                        {message}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-700">
                      👨‍⚕️ Doctor consultations • 💊 Medicine delivery • 🧪 Lab
                      tests
                    </div>
                  )}
                </div>
              </div>

              {/* CTA */}
              <Link to="/doctors">
                <Button
                  size="lg"
                  className="bg-orange-600 hover:bg-orange-700 text-lg px-6 py-3"
                >
                  Find Doctors Near You
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1666886573452-9dc8ce8f5cc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc1ODgxOTg5Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Healthcare Professional"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rest of the sections remain unchanged */}
      {/* Quick Links */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Healthcare Services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {quickLinks.map((link) => (
              <Link key={link.name} to={link.href}>
                <Card className="group hover:shadow-lg transition-all duration-300 border-orange-100 hover:border-orange-300 h-full">
                  <CardContent className="p-4 md:p-6 text-center">
                    <div
                      className={`${link.color} w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:scale-110 transition-transform`}
                    >
                      <link.icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">
                      {link.name}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600">
                      {link.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe Widget */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-orange-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-3xl text-gray-900">
                Become a Subscriber
              </CardTitle>
              <CardDescription className="text-lg text-gray-600">
                Get 50% off on lab tests, free home delivery, and priority
                healthcare support
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={handleSubscribe}
                className="grid grid-cols-1 md:grid-cols-4 gap-4"
              >
                <Input
                  placeholder="Full Name"
                  value={subscribeForm.name}
                  onChange={(e) =>
                    setSubscribeForm({ ...subscribeForm, name: e.target.value })
                  }
                  required
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={subscribeForm.email}
                  onChange={(e) =>
                    setSubscribeForm({
                      ...subscribeForm,
                      email: e.target.value,
                    })
                  }
                  required
                />
                <Input
                  type="tel"
                  placeholder="Phone"
                  value={subscribeForm.phone}
                  onChange={(e) =>
                    setSubscribeForm({
                      ...subscribeForm,
                      phone: e.target.value,
                    })
                  }
                  required
                />
                <Button
                  type="submit"
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Subscribe Free
                </Button>
              </form>
              <p className="text-xs text-gray-500 mt-4 text-center">
                * Free subscription with instant benefits. No hidden charges.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Why Choose GEETH365?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="text-center border-orange-100 hover:border-orange-300 transition-colors"
              >
                <CardContent className="p-6 md:p-8">
                  <div className="bg-orange-100 w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                    <feature.icon className="w-6 h-6 md:w-8 md:h-8 text-orange-600" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency & Medical Help Contact */}
      <section className="py-12 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Medical Emergency */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-4">
                Medical Emergency
              </h2>
              <p className="text-red-100 mb-6">
                For immediate medical emergencies, contact local emergency
                services
              </p>

              <div className="space-y-3">
                <a href="tel:108" className="block">
                  <Button
                    size="lg"
                    className="w-full bg-white text-red-600 hover:bg-gray-100"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Emergency Ambulance: 108
                  </Button>
                </a>
                <a href="tel:112" className="block">
                  <Button
                    size="lg"
                    className="w-full bg-white text-red-600 hover:bg-gray-100"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Emergency Helpline: 112
                  </Button>
                </a>
                <a href="tel:100" className="block">
                  <Button
                    size="lg"
                    className="w-full bg-white text-red-600 hover:bg-gray-100"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Police Emergency: 100
                  </Button>
                </a>
              </div>
            </div>

            {/* Medical Help & Consultation */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-4">
                Medical Help & Consultation
              </h2>
              <p className="text-red-100 mb-6">
                For medical queries, health guidance, and doctor consultations
              </p>

              <div className="space-y-3">
                <a
                  href="https://wa.me/917337541113?text=Need medical consultation and guidance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button
                    size="lg"
                    className="w-full bg-green-500 text-white hover:bg-green-600"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Medical Help: 7337541113
                  </Button>
                </a>
                <a href="tel:7337541113" className="block">
                  <Button
                    size="lg"
                    className="w-full bg-white text-red-600 hover:bg-gray-100"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call for Medical Guidance
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
